import Vue from 'vue';
import Vuex from 'vuex';

import actService from '@/services/actService';
import mbc from './mbc';
import sso from './sso';

Vue.use(Vuex);

export default new Vuex.Store({
  modules: {
    mbc,
    sso,
  },
  state: {
    isMobile: false,
    companyId: '',
    memberSourceId: '',
    accessToken: '',
    memberId: '',
    activityId: '',
    activity: null,
    activityWithoutPromise: null,
    activityResultId: '',
    activityResult: null,
    // ACT相關系統參數
    parameters: null,
    showForm: false,
    prizeTitle: '',
    prizeImage: '',
    isReceivingFormComplete: true,
    AwardInformation: {
      isAddress: false,
      isEmail: false,
      isIdentityNumber: false,
      isMobile: false,
      isName: false,
      isFillin: false,
    },
    MgmData: {},
    termsType: '',
    showTerms: false,
    loading: false,
    // 判斷resultHeader是否有資料
    isComplete: false,
  },
  getters: {
    // 娛樂活動資料(刮刮樂...)
    entertainmentInfo: (state) => () => {
      if (state.activityWithoutPromise) {
        switch (state.activityWithoutPromise.categoryID) {
          // 刮刮樂
          case 'Scratchoff':
            return state.activityWithoutPromise.scratchOffInfo;
          // 拉霸
          case 'Slot':
            return state.activityWithoutPromise.slotInfo;
          // 輪盤
          case 'Roulette':
            return state.activityWithoutPromise.rouletteInfo;
          default:
            return {};
        }
      }

      return {};
    },
    hasForm: (state) => () => (
      state.AwardInformation
      && state.AwardInformation.isFillin
      && (state.AwardInformation.isAddress
        || state.AwardInformation.isEmail
        || state.AwardInformation.isIdentityNumber
        || state.AwardInformation.isMobile
        || state.AwardInformation.isName)
    ),
  },
  mutations: {
    set(state, { name, value }) {
      state[name] = value;
    },
    setAwardInformation(state, { name, value }) {
      state.AwardInformation[name] = value;
    },
    push(state, { name, value }) {
      state[name].push(value);
    },
    delete(state, { name }) {
      state[name] = null;
    },
    setShowTerms(state, type) {
      state.showTerms = true;
      state.termsType = type;
    },
  },
  actions: {
    // 取得活動資料
    getActivity: ({ state, commit }) => {
      // 若state中沒有活動資料的promise，呼叫API
      if (!state.activity) {
        const param = {
          companyID: state.companyId,
          memberSourceID: state.memberSourceId,
          id: state.activityId,
          activeType: 1,
        };
        const activity = actService.getActivity(param)
          .toPromise()
          .then((res) => {
            commit('set', { name: 'activityWithoutPromise', value: res.response.data[0] });
            return res.response.data[0];
          });
        commit('set', { name: 'activity', value: activity });
      }

      // 若state中已有活動資料的promise，直接回傳
      return state.activity;
    },
    // 取得所有ACT的系統參數
    getAllParameter: ({ state, commit }) => {
      // 若state中沒有活動資料的promise，呼叫API
      if (!state.parameters) {
        const parameters = actService.getActivityParameter()
          .toPromise()
          .then((res) => res.response.data);
        commit('set', { name: 'parameters', value: parameters });
      }

      // 若state中已有活動資料的promise，直接回傳
      return state.parameters;
    },
    // 取得活動結果資料(複數)
    getActivityResult: ({ commit, getters, state }, activityNo) => {
      const param = {
        memberID: state.memberId,
        activityNo,
      };
      // retry次數上限
      const RetryLimit = 3;

      let result = actService.getActivityResult(param).toPromise();
      const resultPromise = actService.getActivityResult(param);
      for (let tryCnt = 1; tryCnt < RetryLimit; tryCnt += 1) {
        result = result.catch(() => resultPromise.toPromise());
      }

      return result.then((res) => {
        if (res.response.data.length) {
          commit('set', { name: 'activityResultId', value: res.response.data[0].id });
          commit('set', { name: 'isReceivingFormComplete', value: res.response.data[0].isReceivingFormComplete });
          commit('setAwardInformation', { name: 'isReceivingFormComplete', value: res.response.data[0].isReceivingFormComplete });

          const awardData = state.activityWithoutPromise.item.find((d) => d.no === res.response.data[0].activityItemNo);

          commit('set', { name: 'prizeTitle', value: res.response.data[0].activityItemName });
          commit('set', { name: 'prizeImage', value: res.response.data[0].activityItemImage });

          if (awardData) {
            commit('setAwardInformation', { name: 'isAddress', value: awardData.isAddress });
            commit('setAwardInformation', { name: 'isEmail', value: awardData.isEmail });
            commit('setAwardInformation', { name: 'isIdentityNumber', value: awardData.isIdentityNumber });
            commit('setAwardInformation', { name: 'isMobile', value: awardData.isMobile });
            commit('setAwardInformation', { name: 'isName', value: awardData.isName });
            commit('setAwardInformation', { name: 'isFillin', value: awardData.isFillin });
          }

          if (getters.hasForm()) {
            commit('set', { name: 'showForm', value: true });
          }
        }

        return res.response.data;
      })
        .catch(() => null);
      // toPromise回傳500會進catch 先固定傳null回去給前台處理導去ErrorPage頁面
    },
    // 更新活動結果資料
    updateActivityResult: ({ state }) => {
      const nowTime = new Date().valueOf();
      const param = {
        companyId: state.companyId,
        id: state.activityResultId,
        clickTime: nowTime,
        completeTime: nowTime,
        complete: true,
        memberId: state.memberId,
      };
      return actService.updateActivityResult(param)
        .toPromise()
        // 因會有733的成功情況，故回傳整個response
        .then((res) => res.response);
    },
    patchCommunicationData({ state }, payload = {}) {
      const body = {
        companyId: state.companyId,
        id: state.activityResultId,
        name: payload.name,
        mobile: payload.mobile,
        email: payload.email,
        address: payload.address,
        identityNumber: payload.identityNumber,
      };
      return actService.patchCommunicationData(body)
        .toPromise()
        .then((res) => res.response);
    },
    getMGMData: ({ state, commit }) => {
      const body = {
        companyId: state.companyId,
        memberId: state.memberId,
        memberSourceId: state.memberSourceId,
        activityId: state.activityId,
      };
      return actService.getMGMData(body).toPromise()
        .then((res) => {
          commit('set', { name: 'MgmData', value: res.response.data[0] });
          return res.response.data[0];
        });
    },
    postMGMLog({ state }, activityStatus) {
      const body = {
        companyId: state.companyId,
        memberId: state.memberId,
        memberSourceId: state.memberSourceId,
        activityId: state.activityId,
        activityStatus,
        pageType: 1,
      };
      return actService.postMGMLog(body)
        .toPromise();
    },
    postMGMActivity({ rootState, state }, data) {
      const body = {
        language: navigator.language.toLowerCase(),
        activity: [
          {
            activityId: state.activityId,
            mgm: [
              {
                mgmType: data.type,
                member: [
                  {
                    sourceMemberId: rootState.memberId,
                    memberId: data.memberid,
                  },
                ],
              },
            ],
          },
        ],
      };
      return actService.postMGMActivity(body)
        .toPromise();
    },
  },
});
